import { HEALTH_TIMINGS } from '../health/timings'
import { LOCAL_AGENT_PRODUCT } from './localAgent'

/**
 * Связь с кассовым агентом на этом компьютере: что агент отвечает на `GET /v1/hello` и что окно помнит о связи (RP-SEP3).
 * Общее для окна «Оборудование» и окна продажи «Собственной»: оба находят агента на 127.0.0.1, оба решают по одной и той же
 * фазе связи. Чистые функции без сети и React: фаза выводится из снимка и текущего времени, ничего не опрашивается.
 * Правило честности: «на связи» пишется только если агент действительно ответил.
 */

/** Драйвер АТОЛ по сведениям агента: файлы компьютера браузер не видит, отвечает только агент. */
export interface AtolDriver {
  installed: boolean
  version: string | null
  path: string | null
  /** Почему драйвер не годится, хотя файл найден (например, 32-разрядная библиотека). */
  problem: string | null
}

/** Что агент отвечает на `GET /v1/hello` (без токена): кто он и какие драйверы уже стоят. */
export interface AgentHello {
  version: string
  instanceId: string
  atol: AtolDriver
  oneCConnected: boolean
}

/** Семь фаз связи с агентом (п. 2.5). */
export type LinkPhase = 'searching' | 'connected' | 'lost' | 'missing' | 'choose' | 'outdated' | 'denied'

/** Как запущен агент: службой Windows или вручную из консоли. */
export interface AgentHostInfo {
  mode: 'service' | 'console' | null
  startedAt: string | null
  machine: string | null
  user: string | null
  os: string | null
}

/** `hello` с полями версии 2; если агент их не прислал, `api`, `host`, `originAllowed` равны `null`, а агент считается устаревшим. */
export interface AgentHelloV2 extends AgentHello {
  api: { level: number; features: readonly string[] } | null
  host: AgentHostInfo | null
  /** `false` — агент ответил, но этот сайт не в его списке адресов; `null` — агент о таком не сообщает. */
  originAllowed: boolean | null
  /** Режим рабочего места агента (`own` / `1c`); `null` — агент о нём не сообщает. */
  workplaceMode: string | null
}

export interface AgentRef {
  port: number
  hello: AgentHelloV2
}

/** Что окно помнит о связи; обновляется чистой функцией `applyDiscovery`. */
export interface LinkSnapshot {
  /** Первый просмотр портов завершён. */
  probed: boolean
  /** Все найденные при последнем полном просмотре. */
  agents: readonly AgentRef[]
  /** Выбранный агент (в памяти страницы); последние известные данные остаются, пока связь потеряна. */
  selected: AgentRef | null
  lastSeenAt: number | null
  /** Сколько просмотров подряд агент не ответил. */
  misses: number
  /** Когда связь объявлена потерянной (второй промах подряд). */
  lostSince: number | null
  /** 401/403 на `/v1/equipment`. */
  equipmentDenied: boolean
  /** Нужный окну уровень `api.level` (из настроек приложения). */
  minApiLevel: number
  /** Адрес сайта: показывается, когда агент его не пускает. */
  siteOrigin: string
}

/** Сколько промахов подряд нужно, чтобы связь считалась потерянной (одиночный таймаут не должен мигать). */
export const LOST_AFTER_MISSES = 2

export function initialLink(siteOrigin: string, minApiLevel: number): LinkSnapshot {
  return {
    probed: false,
    agents: [],
    selected: null,
    lastSeenAt: null,
    misses: 0,
    lostSince: null,
    equipmentDenied: false,
    minApiLevel,
    siteOrigin,
  }
}

/** Тот же агент: порт и установка (`instanceId`) совпали. */
export const sameAgent = (left: AgentRef, right: AgentRef): boolean =>
  left.port === right.port && left.hello.instanceId === right.hello.instanceId

/**
 * Итог одного полного просмотра портов. Нашли хоть кого-то — промахи обнуляются сразу, одним ответом (возврат агента
 * не требует действий человека). Не нашли — копим промахи; выбранный агент и его последние данные остаются.
 */
export function applyDiscovery(link: LinkSnapshot, found: readonly AgentRef[], now: number): LinkSnapshot {
  if (found.length === 0) {
    const misses = link.misses + 1
    return {
      ...link,
      probed: true,
      agents: [],
      misses,
      lostSince: misses >= LOST_AFTER_MISSES ? (link.lostSince ?? now) : link.lostSince,
    }
  }
  const previous = link.selected
  const kept = previous === null ? undefined : found.find((agent) => sameAgent(agent, previous))
  const selected = kept ?? (found.length === 1 ? found[0] : null)
  return { ...link, probed: true, agents: found, selected: selected ?? null, lastSeenAt: now, misses: 0, lostSince: null }
}

export function apiLevel(agent: AgentRef | null): number {
  return agent?.hello.api?.level ?? 0
}

/** Агент, с которым работает окно: выбранный, а если он один на портах — он. */
export function activeAgent(link: LinkSnapshot): AgentRef | null {
  if (link.selected !== null) return link.selected
  return link.agents.length === 1 ? (link.agents[0] ?? null) : null
}

/** Фаза связи по таблице п. 2.5. */
export function linkPhase(link: LinkSnapshot, now: number): LinkPhase {
  if (!link.probed) return 'searching'
  if (link.agents.length === 0) {
    if (link.lastSeenAt === null) return 'missing'
    if (link.misses < LOST_AFTER_MISSES) return 'connected'
    return now - (link.lostSince ?? link.lastSeenAt) > HEALTH_TIMINGS.agentLostToMissingMs ? 'missing' : 'lost'
  }
  const agent = activeAgent(link)
  if (agent === null) return 'choose'
  if (agent.hello.originAllowed === false || link.equipmentDenied) return 'denied'
  if (apiLevel(agent) < link.minApiLevel) return 'outdated'
  return 'connected'
}


const record = (raw: unknown, what: string): Record<string, unknown> => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new Error(`${what}: ожидался объект`)
  return raw as Record<string, unknown>
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)

/** Драйвер АТОЛ из ответа агента (`hello.drivers.atol`, `GET /v1/equipment`). */
export function decodeAtolDriver(raw: unknown): AtolDriver {
  const value = record(raw, 'драйвер АТОЛ')
  if (typeof value.installed !== 'boolean') throw new Error('драйвер АТОЛ: нет признака installed')
  return {
    installed: value.installed,
    version: text(value.version),
    path: text(value.path),
    problem: text(value.problem),
  }
}

/** Ответ `GET /v1/hello`: берём только то, что нужно окну; чужая программа на порту отсекается раньше, по `product`. */
export function decodeAgentHello(raw: unknown): AgentHello {
  const value = record(raw, 'ответ агента')
  if (value.product !== LOCAL_AGENT_PRODUCT) throw new Error('это не наш агент')
  const drivers = value.drivers === undefined ? {} : record(value.drivers, 'drivers')
  const oneC = value.oneC === undefined ? {} : record(value.oneC, 'oneC')
  return {
    version: text(value.version) ?? '?',
    instanceId: text(value.instanceId) ?? '',
    atol:
      drivers.atol === undefined
        ? { installed: false, version: null, path: null, problem: null }
        : decodeAtolDriver(drivers.atol),
    oneCConnected: oneC.connected === true,
  }
}


const finiteNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

function decodeHost(raw: unknown): AgentHostInfo | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const value = raw as Record<string, unknown>
  return {
    mode: value.mode === 'service' || value.mode === 'console' ? value.mode : null,
    startedAt: text(value.startedAt),
    machine: text(value.machine),
    user: text(value.user),
    os: text(value.os),
  }
}

/**
 * Ответ `GET /v1/hello` с полями версии 2. Агент без `api`/`host`/`originAllowed` («уровень 0») принимается:
 * недостающее — `null`, окно не падает. Урезанный ответ чужому сайту (`originAllowed:false`, без `drivers`) тоже годится.
 */
export function decodeAgentHelloV2(raw: unknown): AgentHelloV2 {
  const base = decodeAgentHello(raw)
  const value = record(raw, 'ответ агента')
  let api: AgentHelloV2['api'] = null
  if (typeof value.api === 'object' && value.api !== null && !Array.isArray(value.api)) {
    const source = value.api as Record<string, unknown>
    const features = Array.isArray(source.features)
      ? (source.features as readonly unknown[]).filter((item): item is string => typeof item === 'string')
      : []
    api = { level: finiteNumber(source.level) ?? 0, features }
  }
  return {
    ...base,
    api,
    host: decodeHost(value.host),
    originAllowed: typeof value.originAllowed === 'boolean' ? value.originAllowed : null,
    workplaceMode: text(value.workplaceMode),
  }
}
